/**
 * Shared API client for the Market Intelligence application.
 *
 * Every service file must use `getApiClient()` instead of creating its own
 * axios instance.  Authentication is handled via HttpOnly session cookies —
 * the browser attaches them automatically.  CSRF protection uses the
 * double-submit pattern: axios reads the `__Host-csrftoken` cookie
 * (`csrftoken` in plain-HTTP dev; see `readCsrfToken`) and sends it
 * as the `X-CSRFToken` header on mutating requests.
 */

import * as Sentry from "@sentry/react";
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

import { fetchServedApiBaseUrl, getConfigAsync } from "../utils/configContext.js";

// ---------------------------------------------------------------------------
// Custom event dispatched on 401 so AuthContext can react without a hard
// page reload.  Components / contexts listen via:
//   window.addEventListener("auth:unauthorized", handler)
// ---------------------------------------------------------------------------
export const AUTH_UNAUTHORIZED_EVENT = "auth:unauthorized";

// ---------------------------------------------------------------------------
// Custom event dispatched when the API's release (X-App-Version response
// header) has a different major.minor than the bundle's own release
// (REACT_APP_SENTRY_RELEASE).  VersionSkewToast listens via:
//   window.addEventListener("app:version-mismatch", handler)
// Dispatched at most once per page load — see `_versionMismatchDispatched`.
// ---------------------------------------------------------------------------
export const APP_VERSION_MISMATCH_EVENT = "app:version-mismatch";

// Tolerant "major.minor" extractor — used for both the API header and the
// bundle's own baked-in release string.
const MAJOR_MINOR_RE = /^v?(\d+)\.(\d+)/;

// The bundle's own major.minor, parsed once from REACT_APP_SENTRY_RELEASE
// (format `mi-web@<version>`). The <version> part varies by build path:
// `1.14.1+<commit>` (docker-bake.hcl CD builds), `1.14.1-5-g<sha>[-dirty]`
// (justfile iac-images builds, git-describe format), or a bare branch name
// (branch-dispatched CD builds — no version to compare, so the check
// disables itself). Undefined in local dev, where the env var is unset.
const BUNDLE_MAJOR_MINOR = (() => {
  const release = process.env.REACT_APP_SENTRY_RELEASE;
  const match = release?.split("@")[1]?.match(MAJOR_MINOR_RE);
  return match ? `${match[1]}.${match[2]}` : undefined;
})();

let _versionMismatchDispatched = false;

/**
 * True once a major.minor skew between the API and this bundle has been
 * detected this page load. Lets a listener that mounts after the (at most
 * once per page load) event was dispatched still learn about the mismatch.
 */
export function hasVersionMismatch(): boolean {
  return _versionMismatchDispatched;
}

// ---------------------------------------------------------------------------
// Transient-failure retry (with backoff) for the shared response interceptor.
//
// A 502/503/504 or a network error (no response at all) means the upstream
// is unavailable — most commonly a backend redeploy taking the pod down for
// a few seconds — and is *not* evidence the session is invalid. Only an
// authoritative 401 (unauthenticated) or 403 (forbidden) from the backend
// itself should log the user out; those are handled separately below and
// are never retried.
//
// Only *idempotent* requests (GET/HEAD/OPTIONS) are replayed. A 502 or a
// dropped connection can happen after the backend already processed a
// write — replaying a POST/PUT/PATCH/DELETE risks double-submitting it
// (e.g. triggering a discovery run twice). A non-idempotent request on a
// retryable error is rejected with the original error instead; it is never
// treated as a logout signal either way, since only 401/403 does that.
// ---------------------------------------------------------------------------
const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const IDEMPOTENT_METHODS = new Set(["get", "head", "options"]);
// 8 retries at 0.5s, 1s, 2s, 4s, then 8s x4 rides out ~39.5s of upstream
// unavailability. 4 retries (~7.5s) was shorter than the API's gap during a
// Nomad deploy, so every deploy surfaced as a user-facing 502 (FRONTEND-7).
const MAX_RETRIES = 8;
const RETRY_BASE_DELAY_MS = 500;
const RETRY_MAX_DELAY_MS = 8000;

type RetryableAxiosRequestConfig = InternalAxiosRequestConfig & {
  __retryCount?: number;
};

/** True for a 502/503/504 response, or a network error with no response at all. */
export function isRetryableError(error: AxiosError): boolean {
  if (!error.response) return true;
  return RETRYABLE_STATUSES.has(error.response.status);
}

/** True for GET/HEAD/OPTIONS — the only methods safe to silently replay. */
export function isIdempotentMethod(method: string | undefined): boolean {
  return IDEMPOTENT_METHODS.has((method ?? "get").toLowerCase());
}

/** True once `retryCount` has already reached the configured cap. */
export function shouldRetry(error: AxiosError, retryCount: number): boolean {
  return (
    retryCount < MAX_RETRIES &&
    isRetryableError(error) &&
    isIdempotentMethod(error.config?.method)
  );
}

/** Exponential backoff (500ms, 1s, 2s, 4s, ...), capped at RETRY_MAX_DELAY_MS. */
export function computeBackoffDelayMs(retryCount: number): number {
  return Math.min(RETRY_BASE_DELAY_MS * 2 ** retryCount, RETRY_MAX_DELAY_MS);
}

/**
 * Only a 401 (unauthenticated) or 403 (forbidden) is an authoritative signal
 * from the backend that the current session is no longer valid. Anything
 * else — including every 5xx and network error — must not clear the
 * session.
 */
export function isAuthoritativeAuthFailure(error: AxiosError): boolean {
  const status = error.response?.status;
  return status === 401 || status === 403;
}

/**
 * The CSRF token Django expects, read from a `document.cookie` string.
 *
 * Deployed (HTTPS) backends name the cookie `__Host-csrftoken`, which the
 * browser never shares across subdomains; plain-HTTP dev keeps `csrftoken`.
 * The `__Host-` cookie wins whenever present, so a legacy `csrftoken` scoped
 * to a parent domain (still in browsers from before the rename) is never
 * sent in its place.
 */
export function readCsrfToken(cookies: string): string | null {
  for (const name of ["__Host-csrftoken", "csrftoken"]) {
    const match = cookies.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
    if (match) {
      return match[1];
    }
  }
  return null;
}

/**
 * After a network error, checks whether the stack now serves a different
 * apiBaseUrl than the one this page loaded with. A tab left open across a
 * deploy that moved the API (e.g. the retired `staging-api` host) otherwise
 * keeps calling a name that no longer resolves, and since no response ever
 * arrives, the X-App-Version skew check cannot fire. When the base moved,
 * the instance (and the failed request, if it is retried) adopt the new one
 * and the reload prompt is raised: the bundle is stale too.
 */
export async function adoptMovedApiBase(
  instance: AxiosInstance,
  config: InternalAxiosRequestConfig | undefined,
  fetchApiBaseUrl: () => Promise<string | null> = fetchServedApiBaseUrl,
): Promise<void> {
  const current = instance.defaults?.baseURL;
  if (!current) return;
  const served = await fetchApiBaseUrl();
  if (!served || served === current) return;

  instance.defaults.baseURL = served;
  if (config) config.baseURL = served;
  dispatchVersionMismatch();
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Builds the response-interceptor error handler bound to `instance`, so a
 * retryable failure can be replayed through the same axios instance.
 * Exported (rather than inlined) so tests can drive it directly with
 * fabricated `AxiosError`s instead of mocking the network.
 */
export function createResponseErrorHandler(
  instance: AxiosInstance,
  fetchApiBaseUrl: () => Promise<string | null> = fetchServedApiBaseUrl,
): (error: AxiosError) => Promise<unknown> {
  return async (error: AxiosError) => {
    checkVersionSkew(
      (error.response?.headers as Record<string, unknown> | undefined)?.["x-app-version"],
    );

    const config = error.config as RetryableAxiosRequestConfig | undefined;
    if (!error.response && !config?.__retryCount) {
      await adoptMovedApiBase(instance, config, fetchApiBaseUrl);
    }
    if (config) {
      const retryCount = config.__retryCount ?? 0;
      if (shouldRetry(error, retryCount)) {
        config.__retryCount = retryCount + 1;
        await delay(computeBackoffDelayMs(retryCount));
        return instance(config);
      }
    }

    if (isAuthoritativeAuthFailure(error)) {
      // Notify listeners (AuthContext) instead of forcing a hard redirect.
      window.dispatchEvent(new Event(AUTH_UNAUTHORIZED_EVENT));
    }

    // Callers catch API errors and only log them, which Sentry never sees.
    // Report the hard failures here, once retries are spent: no response at
    // all, or a 5xx. A 4xx is the API answering, not failing.
    if (isRetryableError(error) || (error.response?.status ?? 0) >= 500) {
      Sentry.captureException(error);
    }

    return Promise.reject(error);
  };
}

function checkVersionSkew(apiVersion: unknown): void {
  if (_versionMismatchDispatched) return;
  if (typeof apiVersion !== "string" || !BUNDLE_MAJOR_MINOR) return;

  const apiMatch = apiVersion.match(MAJOR_MINOR_RE);
  if (!apiMatch) return;

  if (`${apiMatch[1]}.${apiMatch[2]}` !== BUNDLE_MAJOR_MINOR) {
    dispatchVersionMismatch();
  }
}

function dispatchVersionMismatch(): void {
  if (_versionMismatchDispatched) return;
  _versionMismatchDispatched = true;
  window.dispatchEvent(new Event(APP_VERSION_MISMATCH_EVENT));
}

// ---------------------------------------------------------------------------
// Singleton instance — lazily created on first `getApiClient()` call so the
// async config fetch only happens once.
// ---------------------------------------------------------------------------
let _instance: AxiosInstance | null = null;
let _initPromise: Promise<AxiosInstance> | null = null;

async function createInstance(): Promise<AxiosInstance> {
  const config = await getConfigAsync();

  const instance = axios.create({
    baseURL: config.apiBaseUrl,
    timeout: 30000,
    withCredentials: true, // send session cookie cross-origin
    headers: {
      "Content-Type": "application/json",
    },
  });

  // --- request interceptor: attach CSRF token from cookie -----------------
  // Axios's built-in xsrfCookieName does not work cross-origin, so we
  // manually read the csrftoken cookie and set the X-CSRFToken header.
  instance.interceptors.request.use((reqConfig) => {
    const token = readCsrfToken(document.cookie);
    if (token) {
      reqConfig.headers["X-CSRFToken"] = token;
    }
    return reqConfig;
  });

  // --- response interceptor: version-skew check, retry, + auth handling ---
  // A stale bundle most often surfaces as 4xx/5xx errors, and those
  // responses carry X-App-Version too — checked on both success and error
  // paths. Transient 502/503/504/network errors are retried with backoff
  // instead of being treated as a logout signal; see createResponseErrorHandler.
  instance.interceptors.response.use((response) => {
    checkVersionSkew(response.headers?.["x-app-version"]);
    return response;
  }, createResponseErrorHandler(instance));

  // Bootstrap CSRF cookie so the first POST request has it available.
  try {
    await instance.get("/auth/csrf/");
  } catch {
    // Non-fatal — the cookie may already exist from a previous session.
  }

  return instance;
}

/**
 * Returns the shared axios instance, creating it on first call.
 * All service files should use this instead of `axios.create()`.
 */
export async function getApiClient(): Promise<AxiosInstance> {
  if (_instance) return _instance;

  if (!_initPromise) {
    _initPromise = createInstance().then((inst) => {
      _instance = inst;
      return inst;
    });
  }

  return _initPromise;
}
